import React, { useMemo, useRef, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormGroup, Form, Input, Label, Button, Row, Col, Spinner } from "reactstrap";
import { Link, useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ValetApi from "../api/Api";

function useResetToken() {
    const location = useLocation();

    return useMemo(() => {
        const params = new URLSearchParams(location.search || "");
        return String(params.get("token") || "").trim();
    }, [location.search]);
}

const ResetPasswordForm = () => {
    const history = useHistory();
    const location = useLocation();
    const isTenantPortal = new URLSearchParams(location.search).get("portal") === "tenant";
    const token = useResetToken();
    const [loading, setLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);
    const redirectTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (redirectTimerRef.current) {
                clearTimeout(redirectTimerRef.current);
            }
        };
    }, []);

    const formik = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema: Yup.object({
            password: Yup.string().min(8, "Must be at least 8 characters").required("Required"),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password")], "Passwords must match")
                .required("Required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!token) {
                toast.error("Invalid or missing reset token", { autoClose: 1200 });
                return;
            }

            setLoading(true);
            try {
                const res = isTenantPortal ? await ValetApi.resetTenantPassword(token, values.password) : await ValetApi.resetPassword(token, values.password);
                toast.success(res?.data?.message || "Password reset successful.", { autoClose: 1400 });
                setResetSuccess(true);
                resetForm();
                redirectTimerRef.current = setTimeout(() => {
                    history.push(isTenantPortal ? "/tenant/login" : "/login");
                }, 1200);
            } catch (err) {
                toast.error(err?.[0] || "Unable to reset password", { autoClose: 1200 });
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <section className="auth-page">
            {loading ? (
                <Spinner color="primary" />
            ) : (
                <Row className="justify-content-center m-0 w-100">
                    <Col
                        xs={12}
                        sm={10}
                        md={6}
                        lg={4}>
                        <div className="auth-card">
                            <h3>{isTenantPortal ? "Reset Tenant Password" : "Reset Password"}</h3>
                            {!token && <p style={{ color: "#b91c1c", marginBottom: "0.75rem" }}>Missing or invalid token. Please request a new reset link.</p>}
                            {resetSuccess && <p style={{ color: "#166534", marginBottom: "0.75rem" }}>Password reset successful. Redirecting to login...</p>}

                            <Form onSubmit={formik.handleSubmit}>
                                <FormGroup>
                                    <Label htmlFor="password">New Password</Label>
                                    <Input
                                        id="password"
                                        placeholder="enter new password"
                                        name="password"
                                        type="password"
                                        autoComplete="new-password"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.password}
                                    />
                                    {formik.touched.password && formik.errors.password ? <div style={{ color: "red" }}>{formik.errors.password}</div> : null}

                                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        placeholder="confirm new password"
                                        name="confirmPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.confirmPassword}
                                    />
                                    {formik.touched.confirmPassword && formik.errors.confirmPassword ? <div style={{ color: "red" }}>{formik.errors.confirmPassword}</div> : null}
                                </FormGroup>

                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={loading || !token}>
                                    Reset Password
                                </Button>
                            </Form>

                            <div style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
                                <Link to={isTenantPortal ? "/forgot-password?portal=tenant" : "/forgot-password"}>Request a new reset link</Link>
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
        </section>
    );
};

export default ResetPasswordForm;
